import { api } from '@/lib/api'
import { API_PATHS } from '@/constants/api-paths'
import { unmaskMaskedId } from '@/lib/backend-ids'
import { normalizeOrderItemMediaRecord } from '@/lib/order-item-media'
import { normalizePlannerResourceLinks } from '@/lib/planner-links'
import { useAuthStore } from '@/stores/auth-store'
import { SERVICE_INSTANCE_PARAM } from '@/lib/service-instance'
import type {
  ApiResponse,
  PaginatedResponse,
  StoreCategory,
  StoreCategoryPayload,
  StoreAddonGroup,
  StoreAddonGroupPayload,
  StoreAddonItem,
  StoreAddonItemPayload,
  StoreVariantAddonGroupLink,
  StoreVariantAddonGroupLinkPayload,
  StoreVariantAddonItemLink,
  StoreVariantAddonItemLinkPayload,
  StoreOffer,
  StoreOfferPayload,
  StoreOfferScheme,
  StoreOfferSchemePayload,
  StoreOfferProduct,
  StoreOfferProductPayload,
  StorePosOrderPayload,
  StoreProduct,
  StoreProductPayload,
  StoreBrand,
  StoreBrandPayload,
  StoreProductUniqueName,
  StoreProductUniqueNamePayload,
  StoreProductVariant,
  StoreProductVariantMedia,
  StoreProductVariantPrice,
  StoreProductVariantPricePayload,
  StoreProductVariantPayload,
  CountryTaxBundle,
  CountryTaxSystem,
  TaxLabel,
  StoreProductFeature,
  StoreProductFeaturePayload,
  StoreProductFeatureAssignmentSet,
  StoreProductFeatureAssignmentsResponse,
  StoreVariantFeatureAssignmentsResponse,
  StoreOrderItemMedia,
  StoreTag,
  StoreTagPayload,
  StoreTagset,
  StoreTagsetPayload,
  StoreTaggedItemPayload,
  StoreTaggedItem,
  StoreTagItemType,
} from '@/types'

export interface StoreListParams {
  page?: number
  page_size?: number
  search?: string
  ordering?: string
  name?: string
  service_instance_id?: string | number
  is_active?: boolean
  include_inactive?: boolean | number | string
  active?: boolean | number | string
  product?: string | number
  product_id?: string | number
  category?: string | number
  status?: string | number
  scheme?: string | number
  type?: string | number
  service_variant_type?: string | number
  category_type?: string
  label?: string
  summary?: boolean | number | string
  compact?: boolean | number | string
  include?: string
}

export interface StoreCategoryHierarchyNode {
  id: string | number
  _id?: string | number | null
  parent_id?: string | number | null
  _parent_id?: string | number | null
  name?: string | null
  slug?: string | null
  description?: string | null
  short_code?: string | null
  sorting_order?: number | null
  logo?: string | null
  children?: StoreCategoryHierarchyNode[]
}

export interface StoreTagLinkedEntry {
  id: string | number
  _id?: string | number | null
  name?: string | null
  description?: string | null
  logo?: string | null
}

export interface StoreTagLinkedItems {
  id: string | number
  _id?: string | number | null
  code?: string | null
  title?: string | null
  logo?: string | null
  icon?: string | null
  product_data?: StoreTagLinkedEntry[]
  product_variant_data?: StoreTagLinkedEntry[]
  category_data?: StoreTagLinkedEntry[]
  offer_data?: StoreTagLinkedEntry[]
}

export type StoreSettingsRecord = Record<string, unknown> & {
  id?: string | number
  service_instance?: string | number
  guest_checkout_enabled?: boolean | number | string
  order_review_enabled?: boolean | number | string
  product_review_enabled?: boolean | number | string
  whats_app_integration?: boolean | number | string
  chatbot_integration?: boolean | number | string
  post_code_lookup?: boolean | number | string
  slack_integration_enabled?: boolean | number | string
  tax_enabled?: boolean | number | string
  pay_later_enabled?: boolean | number | string
  inventory_mgmt?: boolean | number | string
  out_of_stock_enabled?: boolean | number | string
  reward_system_enabled?: boolean | number | string
  mobile_app_enabled?: boolean | number | string
  catalogue_enabled?: boolean | number | string
  pos_enabled?: boolean | number | string
  order_schedule_type?: string | number | null
  minimum_service_time_minutes?: string | number | null
  notify_before_minutes?: string | number | null
  checkout_limit?: 'one' | 'many' | string | null
}

export type BranchSettingsRecord = Record<string, unknown> & {
  id?: string | number
  service_instance?: string | number
  order_review_enabled?: boolean | number | string
  product_review_enabled?: boolean | number | string
  min_purchase_amount_enabled?: boolean | number | string
  min_purchase_delivery_order?: string | number | null
  home_page_text_enabled?: boolean | number | string
  offer_logo_on_product_card?: boolean | number | string
  tax_enabled?: boolean | number | string
  pay_later_enabled?: boolean | number | string
  printer_enabled?: boolean | number | string
}

const unwrapData = <T>(payload: ApiResponse<T> | T): T => {
  if (!payload || typeof payload !== 'object') {
    return payload as T
  }

  const record = payload as Record<string, unknown>

  // Prefer paginated/list payloads even when an empty `data` field is present.
  if (Array.isArray(record.results) || Array.isArray(record.data)) {
    return payload as T
  }

  if ('data' in record) {
    const inner = record.data
    if (inner !== undefined && inner !== null && inner !== '') {
      return inner as T
    }
  }

  return payload as T
}

const unwrapListPayload = <T>(payload: unknown): T[] => {
  if (Array.isArray(payload)) {
    return payload as T[]
  }

  if (!payload || typeof payload !== 'object') {
    return []
  }

  const record = payload as Record<string, unknown>

  if (Array.isArray(record.data)) {
    return record.data as T[]
  }

  if (Array.isArray(record.results)) {
    return record.results as T[]
  }

  // Fallback for ApiResponse wrappers that still nest under `data`.
  if ('data' in record) {
    const inner = record.data
    if (Array.isArray(inner)) {
      return inner as T[]
    }
  }

  return []
}

const normalizeProductVariantRecord = (variant: StoreProductVariant): StoreProductVariant => {
  const record = variant as StoreProductVariant & {
    product_variant_logo?: string | null
    logo_url?: string | null
    image?: string | null
    thumbnail?: string | null
    product_variant_description?: string | null
  }
  const rawLogo = variant.logo ?? record.product_variant_logo ?? record.logo_url ?? record.image ?? record.thumbnail ?? null
  const normalizedVariant = {
    ...variant,
    description: variant.description ?? record.product_variant_description ?? null,
  }
  if (typeof rawLogo !== 'string' || !rawLogo.trim()) {
    return normalizedVariant
  }

  return {
    ...normalizedVariant,
    logo: rawLogo,
    product_variant_logo: rawLogo,
  } as StoreProductVariant
}

const normalizeProductVariantMediaRecord = (media: StoreProductVariantMedia): StoreProductVariantMedia => ({
  ...media,
  logo: media.logo ?? null,
})

const toCategoryPayload = (payload: StoreCategoryPayload | Partial<StoreCategoryPayload>) => {
  const next: Record<string, unknown> = { ...payload }
  if (next.parent === null || next.parent === undefined || next.parent === '') {
    delete next.parent
  }

  if ('is_active' in next) {
    const isActive = next.is_active
    delete next.is_active
    if (isActive !== undefined) {
      next.active = isActive === true || isActive === 'true' || isActive === 1 ? 1 : 0
    }
  }

  if ('active' in next && next.active !== undefined) {
    const active = next.active
    next.active = active === true || active === 'true' || active === 1 ? 1 : 0
  }
  return next
}

const toProductPayload = (payload: Partial<StoreProductPayload> & { active?: boolean | number | string }) => {
  const next: Record<string, unknown> = { ...payload }

  if ('is_active' in next) {
    const isActive = next.is_active
    delete next.is_active
    if (isActive !== undefined) {
      next.active = isActive === true || isActive === 'true' || isActive === 1 ? 1 : 0
    }
  }

  if ('active' in next && next.active !== undefined) {
    const active = next.active
    next.active = active === true || active === 'true' || active === 1 ? 1 : 0
  }

  if ('deactivate_variants' in next && next.deactivate_variants !== undefined) {
    const deactivateVariants = next.deactivate_variants
    next.deactivate_variants = deactivateVariants === true || deactivateVariants === 'true' || deactivateVariants === 1 ? 1 : 0
  }

  return next
}

const toList = <T>(payload: unknown): T[] => {
  if (Array.isArray(payload)) {
    return payload as T[]
  }

  if (payload && typeof payload === 'object') {
    const record = payload as Record<string, unknown>

    if (Array.isArray(record.results)) {
      return record.results as T[]
    }

    if (Array.isArray(record.data)) {
      return record.data as T[]
    }
  }

  return []
}

const toPaged = <T>(payload: unknown): PaginatedResponse<T> => {
  if (payload && typeof payload === 'object' && 'results' in (payload as Record<string, unknown>)) {
    const paged = payload as PaginatedResponse<T>

    return {
      count: typeof paged.count === 'number' ? paged.count : Array.isArray(paged.results) ? paged.results.length : 0,
      next: paged.next ?? null,
      previous: paged.previous ?? null,
      results: Array.isArray(paged.results) ? paged.results : [],
    }
  }

  const results = toList<T>(payload)

  return {
    count: results.length,
    next: null,
    previous: null,
    results,
  }
}

const getCurrentPostVariantType = (): string | number | undefined => {
  const { currentPost } = useAuthStore.getState()
  const raw = currentPost?.service_variant_unique_id ?? currentPost?.service_variant_type
  if (raw === undefined || raw === null) {
    return undefined
  }
  const trimmed = String(raw).trim()
  return trimmed.length > 0 ? trimmed : undefined
}

const getVariantTypeByServiceInstance = (serviceInstanceId: string | number | undefined): string | number | undefined => {
  if (serviceInstanceId === undefined) {
    return undefined
  }

  const target = String(serviceInstanceId).trim()
  if (!target) {
    return undefined
  }

  const { currentPost, availablePosts } = useAuthStore.getState()
  const posts = [currentPost, ...availablePosts].filter((post): post is NonNullable<typeof currentPost> => Boolean(post))

  const matched = posts.find((post) => {
    const postServiceInstance = String(post.service_instance ?? post.service_instance_id ?? '').trim()
    return postServiceInstance === target
  })

  const raw = matched?.service_variant_unique_id ?? matched?.service_variant_type
  if (raw === undefined || raw === null) {
    return undefined
  }

  const trimmed = String(raw).trim()
  return trimmed.length > 0 ? trimmed : undefined
}

const withVariantTypeParam = <T extends object | undefined>(
  params: T,
  lookup?: { service_instance_id?: string | number },
): Record<string, unknown> => {
  const next: Record<string, unknown> = { ...((params ?? {}) as object) }
  const existing = next.service_variant_type
  if (existing !== undefined && existing !== null && String(existing).trim() !== '') {
    return next
  }

  const fromScopedPost = getVariantTypeByServiceInstance(lookup?.service_instance_id)
  const fromCurrentPost = getCurrentPostVariantType()
  const inferred = fromScopedPost ?? fromCurrentPost
  if (inferred !== undefined) {
    next.service_variant_type = inferred
  }
  return next
}

const withVariantTypePayload = <T extends object>(payload: T): T => {
  const payloadRecord = payload as Record<string, unknown>
  const existing = payloadRecord.service_variant_type
  if (existing !== undefined && existing !== null && String(existing).trim() !== '') {
    return payload
  }

  const scopedServiceInstance = payloadRecord.service_instance
  const inferred = getVariantTypeByServiceInstance(
    scopedServiceInstance === undefined ? undefined : String(scopedServiceInstance),
  ) ?? getCurrentPostVariantType()

  if (inferred === undefined) {
    return payload
  }

  return {
    ...payloadRecord,
    service_variant_type: inferred,
  } as T
}

const ensureVariantTypeParam = <T extends Record<string, unknown> | undefined>(
  params: T,
  lookup?: { service_instance_id?: string | number },
): Record<string, unknown> => {
  const normalized = withVariantTypeParam(params, lookup)
  const variantType = normalized.service_variant_type
  if (variantType === undefined || variantType === null || String(variantType).trim() === '') {
    throw new Error('service_variant_type is required for category operations.')
  }
  return normalized
}

const ensureVariantTypePayload = <T extends Record<string, unknown>>(payload: T): Record<string, unknown> => {
  const normalized = withVariantTypePayload(payload)
  const variantType = normalized.service_variant_type
  if (variantType === undefined || variantType === null || String(variantType).trim() === '') {
    throw new Error('service_variant_type is required for category operations.')
  }
  return normalized
}

const normalizeStoreProduct = (product: StoreProduct & {
  _id?: string | number | null
  active?: boolean | number | string | null
  is_active?: boolean | number | string | null
  category_data?: { id?: string | number | null; name?: string | null } | null
  product_uniquename?: string | null
  product_slug?: string | null
  product_logo?: string | null
  logo_url?: string | null
  image?: string | null
  thumbnail?: string | null
  master_id?: string | number | null
}): StoreProduct => {
  const rawLogo = product.logo ?? product.product_logo ?? product.logo_url ?? product.image ?? product.thumbnail ?? null

  return {
    ...product,
    _id: product._id ?? null,
    raw_id: typeof product.raw_id === 'number' ? product.raw_id : unmaskMaskedId(product._id),
    logo: rawLogo ?? null,
    slug: product.slug ?? product.product_slug ?? null,
    active: product.active ?? product.is_active ?? 1,
    is_active: product.is_active ?? (typeof product.active === 'boolean'
      ? product.active
      : product.active === 0 || product.active === '0'
        ? false
        : true),
    category: product.category ?? product.category_data?.id ?? null,
    category_name: product.category_name ?? product.category_data?.name ?? null,
    master_id: product.master_id ?? null,
    product_unique_name_label: product.product_unique_name_label ?? product.product_uniquename ?? null,
    planner_resource_links: normalizePlannerResourceLinks(product.planner_resource_links),
  }
}

const firstCategoryValue = (category: Record<string, unknown>, keys: string[]) => {
  for (const key of keys) {
    const value = category[key]
    if (value !== undefined && value !== null && value !== '') {
      return value
    }
  }
  return null
}

const readNestedCategoryId = (value: unknown) => {
  if (!value || typeof value !== 'object') {
    return value
  }

  const record = value as Record<string, unknown>
  return firstCategoryValue(record, ['id', '_id', 'category_id', 'parent_id'])
}

const normalizeCategoryMediaUrl = (value: unknown) => {
  if (typeof value !== 'string' || !value.trim()) {
    return null
  }
  return value
}

const normalizeStoreCategory = (category: StoreCategory): StoreCategory => {
  const record = category as StoreCategory & Record<string, unknown>
  const parent = readNestedCategoryId(firstCategoryValue(record, [
    'parent',
    'parent_id',
    '_parent_id',
    'parent_category',
    'parent_category_id',
    'parent_data',
  ]))
  const logo = normalizeCategoryMediaUrl(firstCategoryValue(record, [
    'logo',
    'category_logo',
    'image',
    'thumbnail',
    'icon',
  ]))
  const icon = normalizeCategoryMediaUrl(firstCategoryValue(record, ['icon', 'logo', 'category_logo', 'image', 'thumbnail']))

  return {
    ...category,
    parent: parent == null ? category.parent ?? null : parent as string | number,
    parent_id: parent == null ? record.parent_id as string | number | null | undefined : parent as string | number,
    logo,
    icon,
  }
}

const appendFormDataValue = (formData: FormData, key: string, value: unknown) => {
  if (value === undefined || value === null || value === '') {
    return
  }

  if (typeof value === 'boolean') {
    formData.append(key, value ? 'true' : 'false')
    return
  }

  formData.append(key, String(value))
}

const toCategoryFormData = (
  payload: Record<string, unknown>,
  logoFile?: File | null,
  removeLogo?: boolean,
): FormData => {
  const formData = new FormData()

  Object.entries(payload).forEach(([key, value]) => {
    appendFormDataValue(formData, key, value)
  })

  if (removeLogo) {
    formData.append('remove_logo', 'true')
    formData.append('logo_removed', 'true')
  }

  if (logoFile) {
    formData.append('logo', logoFile)
  }

  return formData
}

const toProductFormData = (
  payload: Partial<StoreProductPayload> & { service_variant_type?: string | number },
  logoFile?: File | null,
  removeLogo?: boolean,
): FormData => {
  const normalizedPayload = toProductPayload(payload)
  const formData = new FormData()

  appendFormDataValue(formData, 'name', normalizedPayload.name)
  appendFormDataValue(formData, 'slug', normalizedPayload.slug)
  appendFormDataValue(formData, 'short_code', normalizedPayload.short_code)
  appendFormDataValue(formData, 'description', normalizedPayload.description)
  appendFormDataValue(formData, 'category', normalizedPayload.category)
  appendFormDataValue(formData, 'master_product_id', normalizedPayload.master_product_id)
  appendFormDataValue(formData, 'brand', normalizedPayload.brand)
  appendFormDataValue(formData, 'product_unique_name', normalizedPayload.product_unique_name)
  appendFormDataValue(formData, 'sku', normalizedPayload.sku)
  appendFormDataValue(formData, 'product_type', normalizedPayload.product_type)
  appendFormDataValue(formData, 'tax_label', normalizedPayload.tax_label)
  appendFormDataValue(formData, 'active', normalizedPayload.active)
  appendFormDataValue(formData, 'service_variant_type', normalizedPayload.service_variant_type)
  appendFormDataValue(formData, 'remove_logo', removeLogo ? true : undefined)
  appendFormDataValue(formData, 'logo_removed', removeLogo ? true : undefined)

  if (logoFile) {
    formData.append('logo', logoFile)
  }

  return formData
}

const toVariantFormData = (payload: any, logoFile?: File | null): FormData => {
  const formData = new FormData()

  appendFormDataValue(formData, 'product', payload.product)
  appendFormDataValue(formData, 'label', payload.label)
  appendFormDataValue(formData, 'name', payload.name)
  appendFormDataValue(formData, 'sku', payload.sku)
  appendFormDataValue(formData, 'price', payload.price)
  appendFormDataValue(formData, 'mrp_price', payload.mrp_price)
  appendFormDataValue(formData, 'sell_price', payload.sell_price)
  appendFormDataValue(formData, 'buy_price', payload.buy_price)
  appendFormDataValue(formData, 'quantity', payload.quantity)
  appendFormDataValue(formData, 'tax_label', payload.tax_label)
  appendFormDataValue(formData, 'taxes_included', payload.taxes_included)
  if ('description' in payload && payload.description !== undefined && payload.description !== null) {
    formData.append('description', String(payload.description))
  }
  appendFormDataValue(formData, 'active', payload.active)
  appendFormDataValue(formData, 'processing_time', payload.processing_time)
  appendFormDataValue(formData, 'processing_unit', payload.processing_unit)
  if (payload.planner_model === null) {
    formData.append('planner_model', '')
  } else {
    appendFormDataValue(formData, 'planner_model', payload.planner_model)
  }
  appendFormDataValue(formData, 'is_active', payload.is_active)
  appendFormDataValue(formData, 'remove_logo', payload.remove_logo)
  appendFormDataValue(formData, 'logo_removed', payload.logo_removed)

  if (logoFile) {
    formData.append('logo', logoFile)
  }

  return formData
}

export const storeCommerceApi = {
  async listStoreSettings(params?: { service_instance_id?: string | number }): Promise<StoreSettingsRecord[]> {
    const response = await api.get<ApiResponse<StoreSettingsRecord[] | PaginatedResponse<StoreSettingsRecord>> | StoreSettingsRecord[] | PaginatedResponse<StoreSettingsRecord>>(
      API_PATHS.store.storeSettings.list,
      {
        params: {
          service_instance_id: params?.service_instance_id,
          org_instance: params?.service_instance_id,
        },
      },
    )
    return toList<StoreSettingsRecord>(unwrapData(response.data))
  },

  async createStoreSettings(payload: Record<string, unknown>): Promise<StoreSettingsRecord> {
    const response = await api.post<ApiResponse<StoreSettingsRecord> | StoreSettingsRecord>(
      API_PATHS.store.storeSettings.create,
      payload,
      { params: { service_instance_id: payload.service_instance } },
    )
    return unwrapData(response.data) as StoreSettingsRecord
  },

  async updateStoreSettings(id: string | number, payload: Record<string, unknown>): Promise<StoreSettingsRecord> {
    const response = await api.patch<ApiResponse<StoreSettingsRecord> | StoreSettingsRecord>(
      API_PATHS.store.storeSettings.update(id),
      payload,
      { params: { service_instance_id: payload.service_instance } },
    )
    return unwrapData(response.data) as StoreSettingsRecord
  },

  async listBranchSettings(params?: { service_instance_id?: string | number }): Promise<BranchSettingsRecord[]> {
    const response = await api.get<ApiResponse<BranchSettingsRecord[] | PaginatedResponse<BranchSettingsRecord>> | BranchSettingsRecord[] | PaginatedResponse<BranchSettingsRecord>>(
      API_PATHS.store.branchSettings.list,
      { params: { service_instance_id: params?.service_instance_id } },
    )
    return toList<BranchSettingsRecord>(unwrapData(response.data))
  },

  async createBranchSettings(payload: Record<string, unknown>): Promise<BranchSettingsRecord> {
    const response = await api.post<ApiResponse<BranchSettingsRecord> | BranchSettingsRecord>(
      API_PATHS.store.branchSettings.create,
      payload,
      { params: { service_instance_id: payload.service_instance } },
    )
    return unwrapData(response.data) as BranchSettingsRecord
  },

  async updateBranchSettings(id: string | number, payload: Record<string, unknown>): Promise<BranchSettingsRecord> {
    const response = await api.patch<ApiResponse<BranchSettingsRecord> | BranchSettingsRecord>(
      API_PATHS.store.branchSettings.update(id),
      payload,
      { params: { service_instance_id: payload.service_instance } },
    )
    return unwrapData(response.data) as BranchSettingsRecord
  },

  async listCountryTaxSystems(params?: {
    country_id?: string | number
    active?: boolean | number | string
  }): Promise<CountryTaxSystem[]> {
    const response = await api.get<ApiResponse<CountryTaxSystem[] | PaginatedResponse<CountryTaxSystem>> | CountryTaxSystem[] | PaginatedResponse<CountryTaxSystem>>(
      API_PATHS.store.taxes.countrySystems,
      {
        params: {
          'country-id': params?.country_id,
          active: params?.active,
          page_size: 1000,
        },
      },
    )
    return toList<CountryTaxSystem>(unwrapData(response.data))
  },

  async listCountryTaxBundles(params?: {
    country_id?: string | number
    tax_system?: string | number
    active?: boolean | number | string
  }): Promise<CountryTaxBundle[]> {
    const response = await api.get<ApiResponse<CountryTaxBundle[] | PaginatedResponse<CountryTaxBundle>> | CountryTaxBundle[] | PaginatedResponse<CountryTaxBundle>>(
      API_PATHS.store.taxes.countryBundles,
      {
        params: {
          'country-id': params?.country_id,
          'tax-system-id': params?.tax_system,
          active: params?.active,
          page_size: 1000,
        },
      },
    )
    return toList<CountryTaxBundle>(unwrapData(response.data))
  },

  async listTaxLabels(params?: {
    service_instance_id?: string | number
    active?: boolean | number | string
  }): Promise<TaxLabel[]> {
    const response = await api.get<ApiResponse<TaxLabel[] | PaginatedResponse<TaxLabel>> | TaxLabel[] | PaginatedResponse<TaxLabel>>(
      API_PATHS.store.taxes.taxLabels,
      {
        params: {
          service_instance_id: params?.service_instance_id,
          active: params?.active,
          page_size: 1000,
        },
      },
    )
    return toList<TaxLabel>(unwrapData(response.data))
  },

  async activateCountryTaxBundle(payload: {
    country_tax_bundle: string | number
    name?: string
    description?: string
    active?: number | boolean
    service_instance_id?: string | number
  }): Promise<TaxLabel> {
    const response = await api.post<ApiResponse<TaxLabel> | TaxLabel>(
      API_PATHS.store.taxes.createTaxLabel,
      {
        country_tax_bundle: payload.country_tax_bundle,
        name: payload.name,
        description: payload.description,
        active: payload.active ?? 1,
      },
      { params: { service_instance_id: payload.service_instance_id } },
    )
    return unwrapData(response.data) as TaxLabel
  },

  async deactivateTaxLabel(id: string | number, params?: { service_instance_id?: string | number }): Promise<TaxLabel> {
    const response = await api.patch<ApiResponse<TaxLabel> | TaxLabel>(
      API_PATHS.store.taxes.updateTaxLabel(id),
      { active: 0 },
      { params: { service_instance_id: params?.service_instance_id } },
    )
    return unwrapData(response.data) as TaxLabel
  },

  async listCategories(params?: StoreListParams & { service_instance_id?: string | number; service_variant_type?: string | number }): Promise<PaginatedResponse<StoreCategory>> {
    const normalizedParams = ensureVariantTypeParam(
      {
        ...params,
        service_variant_type: params?.service_variant_type,
      },
      { service_instance_id: params?.service_instance_id },
    )
    const response = await api.get<ApiResponse<PaginatedResponse<StoreCategory> | StoreCategory[]> | PaginatedResponse<StoreCategory> | StoreCategory[]>(
      API_PATHS.store.categories.list,
      { params: normalizedParams },
    )

    const paged = toPaged<StoreCategory>(unwrapData(response.data))
    return {
      ...paged,
      results: paged.results.map(normalizeStoreCategory),
    }
  },

  async getCategoryHierarchy(params?: {
    is_valid?: boolean
    is_preorder?: boolean
    fixed?: boolean
    service_instance_id?: string | number
    service_variant_type?: string | number
  }): Promise<StoreCategoryHierarchyNode[]> {
    const normalizedParams = ensureVariantTypeParam(
      {
        ...params,
        service_variant_type: params?.service_variant_type,
      },
      { service_instance_id: params?.service_instance_id },
    )
    const response = await api.get<ApiResponse<StoreCategoryHierarchyNode[] | { results?: StoreCategoryHierarchyNode[] }> | StoreCategoryHierarchyNode[] | { results?: StoreCategoryHierarchyNode[] }>(
      API_PATHS.store.categories.hierarchy,
      { params: normalizedParams },
    )

    return toList<StoreCategoryHierarchyNode>(unwrapData(response.data))
  },

  async createCategory(
    payload: StoreCategoryPayload & { service_instance_id?: string | number; service_variant_type?: string | number },
    options?: { logoFile?: File | null },
  ): Promise<{ data: StoreCategory; message?: string }> {
    const normalizedPayload = ensureVariantTypePayload(toCategoryPayload(payload))
    const requestBody = options?.logoFile ? toCategoryFormData(normalizedPayload, options.logoFile) : normalizedPayload
    const response = await api.post<ApiResponse<StoreCategory> | StoreCategory>(
      API_PATHS.store.categories.create,
      requestBody,
      options?.logoFile ? { headers: { 'Content-Type': 'multipart/form-data' } } : undefined,
    )
    const raw = response.data as ApiResponse<StoreCategory>
    return { data: normalizeStoreCategory(unwrapData(response.data)), message: raw?.message }
  },

  async updateCategory(
    id: string | number,
    payload: Partial<StoreCategoryPayload> & { service_instance_id?: string | number; service_variant_type?: string | number },
    options?: { logoFile?: File | null; removeLogo?: boolean },
  ): Promise<StoreCategory> {
    const normalizedPayload = ensureVariantTypePayload(toCategoryPayload(payload))
    const shouldUseFormData = Boolean(options?.logoFile || options?.removeLogo)
    const requestBody = shouldUseFormData ? toCategoryFormData(normalizedPayload, options?.logoFile, options?.removeLogo) : normalizedPayload
    const response = await api.patch<ApiResponse<StoreCategory> | StoreCategory>(
      API_PATHS.store.categories.update(id),
      requestBody,
      shouldUseFormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : undefined,
    )
    return normalizeStoreCategory(unwrapData(response.data))
  },

  async deleteCategory(id: string | number): Promise<void> {
    await api.delete(API_PATHS.store.categories.delete(id))
  },

  async listMasterCategories(params?: StoreListParams & { service_instance_id?: string | number; service_variant_type?: string | number }): Promise<PaginatedResponse<StoreCategory>> {
    const normalizedParams = ensureVariantTypeParam(
      {
        ...params,
        service_variant_type: params?.service_variant_type,
      },
      { service_instance_id: params?.service_instance_id },
    )
    const response = await api.get<ApiResponse<PaginatedResponse<StoreCategory> | StoreCategory[]> | PaginatedResponse<StoreCategory> | StoreCategory[]>(
      API_PATHS.store.categories.masterList,
      { params: normalizedParams },
    )

    const paged = toPaged<StoreCategory>(unwrapData(response.data))
    return {
      ...paged,
      results: paged.results.map(normalizeStoreCategory),
    }
  },

  async createMasterCategory(
    payload: StoreCategoryPayload & { service_instance_id?: string | number; service_variant_type?: string | number },
    options?: { logoFile?: File | null },
  ): Promise<{ data: StoreCategory; message?: string }> {
    const normalizedPayload = ensureVariantTypePayload(toCategoryPayload(payload))
    const requestBody = options?.logoFile ? toCategoryFormData(normalizedPayload, options.logoFile) : normalizedPayload
    const response = await api.post<ApiResponse<StoreCategory> | StoreCategory>(
      API_PATHS.store.categories.masterCreate,
      requestBody,
      options?.logoFile ? { headers: { 'Content-Type': 'multipart/form-data' } } : undefined,
    )
    const raw = response.data as ApiResponse<StoreCategory>
    return { data: normalizeStoreCategory(unwrapData(response.data)), message: raw?.message }
  },

  async updateMasterCategory(
    id: string | number,
    payload: Partial<StoreCategoryPayload> & { service_instance_id?: string | number; service_variant_type?: string | number },
    options?: { logoFile?: File | null; removeLogo?: boolean },
  ): Promise<StoreCategory> {
    const normalizedPayload = ensureVariantTypePayload(toCategoryPayload(payload))
    const shouldUseFormData = Boolean(options?.logoFile || options?.removeLogo)
    const requestBody = shouldUseFormData ? toCategoryFormData(normalizedPayload, options?.logoFile, options?.removeLogo) : normalizedPayload
    const response = await api.patch<ApiResponse<StoreCategory> | StoreCategory>(
      API_PATHS.store.categories.masterUpdate(id),
      requestBody,
      shouldUseFormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : undefined,
    )
    return normalizeStoreCategory(unwrapData(response.data))
  },

  async deleteMasterCategory(id: string | number): Promise<void> {
    await api.delete(API_PATHS.store.categories.masterDelete(id))
  },

  async deriveCategoryFromMasterV2(payload: {
    master_category_ids: Array<string | number>
    parent?: string | number
    include_children?: boolean
    operator_instance?: string | number
  }): Promise<{ data: StoreCategory[]; message?: string }> {
    const response = await api.post<ApiResponse<StoreCategory[] | { data?: StoreCategory[] }> | StoreCategory[] | { data?: StoreCategory[] }>(
      API_PATHS.store.categories.deriveFromMasterV2,
      payload,
    )
    const raw = response.data as ApiResponse<StoreCategory[] | { data?: StoreCategory[] }>
    return {
      data: toList<StoreCategory>(unwrapData(response.data)),
      message: raw?.message,
    }
  },

  async listProducts(params?: StoreListParams): Promise<PaginatedResponse<StoreProduct>> {
    const response = await api.get<ApiResponse<PaginatedResponse<StoreProduct> | StoreProduct[]> | PaginatedResponse<StoreProduct> | StoreProduct[]>(
      API_PATHS.store.products.list,
      { params },
    )

    const paged = toPaged<StoreProduct>(unwrapData(response.data))
    return {
      ...paged,
      results: paged.results.map((product) => normalizeStoreProduct(product)),
    }
  },

  async getProduct(id: string | number, params?: StoreListParams): Promise<StoreProduct> {
    const response = await api.get<ApiResponse<StoreProduct> | StoreProduct>(
      API_PATHS.store.products.detail(id),
      { params },
    )
    return normalizeStoreProduct(unwrapData(response.data))
  },

  async createProduct(payload: StoreProductPayload, options?: { logoFile?: File | null }): Promise<StoreProduct> {
    const requestBody = options?.logoFile ? toProductFormData(payload, options.logoFile) : toProductPayload(payload)
    const response = await api.post<ApiResponse<StoreProduct> | StoreProduct>(API_PATHS.store.products.create, requestBody, options?.logoFile
      ? { headers: { 'Content-Type': 'multipart/form-data' } }
      : undefined)
    return normalizeStoreProduct(unwrapData(response.data))
  },

  async listMasterProducts(params?: StoreListParams): Promise<PaginatedResponse<StoreProduct>> {
    const response = await api.get<ApiResponse<PaginatedResponse<StoreProduct> | StoreProduct[]> | PaginatedResponse<StoreProduct> | StoreProduct[]>(
      API_PATHS.store.products.masterList,
      { params },
    )

    const paged = toPaged<StoreProduct>(unwrapData(response.data))
    return {
      ...paged,
      results: paged.results.map((product) => normalizeStoreProduct(product)),
    }
  },

  async getMasterProduct(id: string | number, params?: StoreListParams): Promise<StoreProduct> {
    const response = await api.get<ApiResponse<StoreProduct> | StoreProduct>(
      API_PATHS.store.products.masterDetail(id),
      { params },
    )
    return normalizeStoreProduct(unwrapData(response.data))
  },

  async createMasterProduct(payload: StoreProductPayload, options?: { logoFile?: File | null }): Promise<StoreProduct> {
    const requestBody = options?.logoFile ? toProductFormData(payload, options.logoFile) : toProductPayload(payload)
    const response = await api.post<ApiResponse<StoreProduct> | StoreProduct>(
      API_PATHS.store.products.masterCreate,
      requestBody,
      options?.logoFile ? { headers: { 'Content-Type': 'multipart/form-data' } } : undefined,
    )
    return normalizeStoreProduct(unwrapData(response.data))
  },

  async updateMasterProduct(id: string | number, payload: Partial<StoreProductPayload>, options?: { logoFile?: File | null; removeLogo?: boolean }): Promise<StoreProduct> {
    const shouldUseFormData = Boolean(options?.logoFile || options?.removeLogo)
    const requestBody = shouldUseFormData ? toProductFormData(payload, options?.logoFile, options?.removeLogo) : toProductPayload(payload)
    const response = await api.patch<ApiResponse<StoreProduct> | StoreProduct>(
      API_PATHS.store.products.masterUpdate(id),
      requestBody,
      shouldUseFormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : undefined,
    )
    return normalizeStoreProduct(unwrapData(response.data))
  },

  async deleteMasterProduct(id: string | number): Promise<void> {
    await api.delete(API_PATHS.store.products.masterDelete(id))
  },

  async updateProduct(id: string | number, payload: Partial<StoreProductPayload>, options?: { logoFile?: File | null; removeLogo?: boolean }): Promise<StoreProduct> {
    const shouldUseFormData = Boolean(options?.logoFile || options?.removeLogo)
    const requestBody = shouldUseFormData ? toProductFormData(payload, options?.logoFile, options?.removeLogo) : toProductPayload(payload)
    const response = await api.patch<ApiResponse<StoreProduct> | StoreProduct>(API_PATHS.store.products.update(id), requestBody, shouldUseFormData
      ? { headers: { 'Content-Type': 'multipart/form-data' } }
      : undefined)
    return normalizeStoreProduct(unwrapData(response.data))
  },

  async deleteProduct(id: string | number): Promise<void> {
    await api.delete(API_PATHS.store.products.delete(id))
  },

  async removeProductLogo(id: string | number): Promise<void> {
    await api.delete(API_PATHS.store.products.removeLogo(id))
  },

  async importProductsCsv(file: File): Promise<Record<string, unknown>> {
    const formData = new FormData()
    formData.append('file', file)

    const response = await api.post<ApiResponse<Record<string, unknown>> | Record<string, unknown>>(
      API_PATHS.store.products.importCsv,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    )

    return unwrapData(response.data)
  },

  async exportProductsCsv(): Promise<{ blob: Blob; filename: string }> {
    const response = await api.get(API_PATHS.store.products.exportCsv, {
      responseType: 'blob',
    })

    const disposition = response.headers['content-disposition']
    const filenameMatch = typeof disposition === 'string'
      ? disposition.match(/filename="?([^\"]+)"?/i)
      : null

    return {
      blob: response.data as Blob,
      filename: filenameMatch?.[1] ?? 'sample_products.csv',
    }
  },

  async listBrands(params?: StoreListParams): Promise<PaginatedResponse<StoreBrand>> {
    const response = await api.get<ApiResponse<PaginatedResponse<StoreBrand> | StoreBrand[]> | PaginatedResponse<StoreBrand> | StoreBrand[]>(
      API_PATHS.store.brands.list,
      { params },
    )

    return toPaged<StoreBrand>(unwrapData(response.data))
  },

  async createBrand(payload: StoreBrandPayload): Promise<StoreBrand> {
    const response = await api.post<ApiResponse<StoreBrand> | StoreBrand>(API_PATHS.store.brands.create, payload)
    return unwrapData(response.data)
  },

  async updateBrand(id: string | number, payload: Partial<StoreBrandPayload>): Promise<StoreBrand> {
    const response = await api.patch<ApiResponse<StoreBrand> | StoreBrand>(API_PATHS.store.brands.update(id), payload)
    return unwrapData(response.data)
  },

  async deleteBrand(id: string | number): Promise<void> {
    await api.delete(API_PATHS.store.brands.delete(id))
  },

  async listProductUniqueNames(params?: StoreListParams): Promise<PaginatedResponse<StoreProductUniqueName>> {
    const response = await api.get<ApiResponse<PaginatedResponse<StoreProductUniqueName> | StoreProductUniqueName[]> | PaginatedResponse<StoreProductUniqueName> | StoreProductUniqueName[]>(
      API_PATHS.store.productUniqueNames.list,
      { params },
    )

    return toPaged<StoreProductUniqueName>(unwrapData(response.data))
  },

  async createProductUniqueName(payload: StoreProductUniqueNamePayload): Promise<StoreProductUniqueName> {
    const response = await api.post<ApiResponse<StoreProductUniqueName> | StoreProductUniqueName>(API_PATHS.store.productUniqueNames.create, payload)
    return unwrapData(response.data)
  },

  async updateProductUniqueName(id: string | number, payload: Partial<StoreProductUniqueNamePayload>): Promise<StoreProductUniqueName> {
    const response = await api.patch<ApiResponse<StoreProductUniqueName> | StoreProductUniqueName>(API_PATHS.store.productUniqueNames.update(id), payload)
    return unwrapData(response.data)
  },

  async deleteProductUniqueName(id: string | number): Promise<void> {
    await api.delete(API_PATHS.store.productUniqueNames.delete(id))
  },

  async listVariants(params?: StoreListParams): Promise<PaginatedResponse<StoreProductVariant>> {
    const response = await api.get<ApiResponse<PaginatedResponse<StoreProductVariant> | StoreProductVariant[]> | PaginatedResponse<StoreProductVariant> | StoreProductVariant[]>(
      API_PATHS.store.variants.list,
      { params },
    )

    const paged = toPaged<StoreProductVariant>(unwrapData(response.data))
    return {
      ...paged,
      results: paged.results.map(normalizeProductVariantRecord),
    }
  },

  async getVariant(id: string | number, params?: Pick<StoreListParams, 'service_instance_id'>): Promise<StoreProductVariant> {
    const response = await api.get<ApiResponse<StoreProductVariant> | StoreProductVariant>(
      API_PATHS.store.variants.detail(id),
      { params },
    )

    return normalizeProductVariantRecord(unwrapData(response.data))
  },

  async createVariant(
    payload: StoreProductVariantPayload, 
    options?: { logoFile?: File | null; service_instance_id?: string | number }
  ): Promise<StoreProductVariant> {
    const requestBody = options?.logoFile ? toVariantFormData(payload, options.logoFile) : payload
    const params = options?.service_instance_id 
      ? { [SERVICE_INSTANCE_PARAM]: options.service_instance_id }
      : undefined
    const response = await api.post<ApiResponse<StoreProductVariant> | StoreProductVariant>(
      API_PATHS.store.variants.create,
      requestBody,
      {
        params,
        headers: options?.logoFile ? { 'Content-Type': 'multipart/form-data' } : undefined,
      },
    )
    const data = unwrapData(response.data) as StoreProductVariant | { product_variant?: StoreProductVariant }
    const variant = data && typeof data === 'object' && 'product_variant' in data
      ? data.product_variant
      : data
    return normalizeProductVariantRecord(variant as StoreProductVariant)
  },

  async updateVariant(
    id: string | number,
    payload: Partial<StoreProductVariantPayload>,
    options?: { logoFile?: File | null; removeLogo?: boolean; service_instance_id?: string | number },
  ): Promise<StoreProductVariant> {
    const shouldUseFormData = Boolean(options?.logoFile)
    const requestBody = shouldUseFormData
      ? toVariantFormData(payload, options?.logoFile ?? undefined)
      : payload
    
    const params = options?.service_instance_id 
      ? { [SERVICE_INSTANCE_PARAM]: options.service_instance_id }
      : undefined
    
    const response = await api.patch<ApiResponse<StoreProductVariant> | StoreProductVariant>(
      API_PATHS.store.variants.update(id),
      requestBody,
      {
        params,
        headers: shouldUseFormData ? { 'Content-Type': 'multipart/form-data' } : undefined,
      },
    )
    const rawUpdated = unwrapData(response.data) as StoreProductVariant
    let normalized = normalizeProductVariantRecord(rawUpdated)

    if (options?.logoFile) {
      const rawUpdatedRecord = rawUpdated as StoreProductVariant & Record<string, unknown>
      const responseLooksPartial = !rawUpdatedRecord.product && !rawUpdatedRecord.label && !rawUpdatedRecord.name

      if (responseLooksPartial) {
        try {
          const detailResponse = await api.get<ApiResponse<StoreProductVariant> | StoreProductVariant>(
            API_PATHS.store.variants.detail(id),
            { params },
          )
          const detail = normalizeProductVariantRecord(unwrapData(detailResponse.data))
          const detailRecord = detail as StoreProductVariant & { product_variant_logo?: string | null }
          const mergedDetail = {
            ...detail,
            logo: rawUpdated.logo ?? detail.logo,
            product_variant_logo: rawUpdated.logo ?? detailRecord.product_variant_logo,
          } as StoreProductVariant & { product_variant_logo?: string | null }
          normalized = normalizeProductVariantRecord(mergedDetail)
        } catch {
          normalized = normalizeProductVariantRecord({
            ...payload,
            ...rawUpdated,
            id,
          } as StoreProductVariant)
        }
      }
    }

    // Variant update endpoint does not reliably clear the logo from remove flags.
    // Use the dedicated remove-logo endpoint when requested.
    if (options?.removeLogo && !options?.logoFile) {
      await api.delete(API_PATHS.store.variants.removeLogo(id), { params })
      return normalizeProductVariantRecord({
        ...normalized,
        logo: null,
        product_variant_logo: null,
      } as StoreProductVariant)
    }

    return normalized
  },

  async deleteVariant(id: string | number): Promise<void> {
    await api.delete(API_PATHS.store.variants.delete(id))
  },

  async removeVariantLogo(
    id: string | number,
    options?: { service_instance_id?: string | number },
  ): Promise<void> {
    const params = options?.service_instance_id
      ? { [SERVICE_INSTANCE_PARAM]: options.service_instance_id }
      : undefined
    await api.delete(API_PATHS.store.variants.removeLogo(id), { params })
  },

  async listVariantMedia(
    variantId: string | number,
    options?: { type?: string | number; service_instance_id?: string | number },
  ): Promise<StoreProductVariantMedia[]> {
    const params = {
      ...(options?.type !== undefined ? { type: options.type } : {}),
      ...(options?.service_instance_id ? { [SERVICE_INSTANCE_PARAM]: options.service_instance_id } : {}),
    }
    const response = await api.get<ApiResponse<StoreProductVariantMedia[]> | StoreProductVariantMedia[]>(
      API_PATHS.store.variants.mediaList(variantId),
      { params },
    )
    return toList<StoreProductVariantMedia>(unwrapData(response.data)).map(normalizeProductVariantMediaRecord)
  },

  async createVariantMedia(payload: {
    product_variant: string | number
    type: string | number
    files: File[]
    service_instance_id?: string | number
  }): Promise<StoreProductVariantMedia[]> {
    const formData = new FormData()
    formData.append('product_variant', String(payload.product_variant))
    formData.append('type', String(payload.type))
    payload.files.forEach((file) => formData.append('logo', file))

    const response = await api.post<ApiResponse<StoreProductVariantMedia[]> | StoreProductVariantMedia[]>(
      API_PATHS.store.variants.mediaCreate,
      formData,
      {
        params: payload.service_instance_id ? { [SERVICE_INSTANCE_PARAM]: payload.service_instance_id } : undefined,
        headers: { 'Content-Type': 'multipart/form-data' },
      },
    )
    return toList<StoreProductVariantMedia>(unwrapData(response.data)).map(normalizeProductVariantMediaRecord)
  },

  async deleteVariantMedia(
    id: string | number,
    options?: { service_instance_id?: string | number },
  ): Promise<void> {
    const params = options?.service_instance_id
      ? { [SERVICE_INSTANCE_PARAM]: options.service_instance_id }
      : undefined
    await api.delete(API_PATHS.store.variants.mediaDelete(id), { params })
  },

  async deactivateVariant(id: string | number): Promise<void> {
    await api.post(API_PATHS.store.variants.deactivate, { id, active: 0 })
  },

  async activateVariant(id: string | number): Promise<void> {
    await api.post(API_PATHS.store.variants.deactivate, { id, active: 1 })
  },

  async listTagsets(params?: StoreListParams & { org_instance?: string | number; item_types?: string | number }): Promise<PaginatedResponse<StoreTagset>> {
    const response = await api.get<
      ApiResponse<PaginatedResponse<StoreTagset> | StoreTagset[]>
      | PaginatedResponse<StoreTagset>
      | StoreTagset[]
    >(
      API_PATHS.store.tagsets.list,
      { params },
    )

    return toPaged<StoreTagset>(unwrapData(response.data))
  },

  /**
   * Lists tagsets for the given item type plus All-type tagsets.
   * Do not pass org_instance here: it is interpreted as the active service
   * instance by auth middleware and breaks session/post validation.
   */
  async listTagsetsForItemType(
    itemType: StoreTagItemType,
    params?: Omit<StoreListParams, 'item_types'> & { org_instance?: string | number },
  ): Promise<PaginatedResponse<StoreTagset>> {
    const response = await this.listTagsets({
      page: 1,
      page_size: 500,
      active: 1,
      item_types: itemType,
      ...params,
    })

    const results = [...(response.results ?? [])].sort((left, right) =>
      String(left.name ?? left.code ?? '').localeCompare(String(right.name ?? right.code ?? '')),
    )

    return {
      ...response,
      count: results.length,
      results,
    }
  },

  async getTagset(id: string | number): Promise<StoreTagset> {
    const response = await api.get<ApiResponse<StoreTagset> | StoreTagset>(
      API_PATHS.store.tagsets.detail(id),
    )

    return unwrapData(response.data)
  },

  async createTagset(payload: StoreTagsetPayload): Promise<StoreTagset> {
    const response = await api.post<ApiResponse<StoreTagset> | StoreTagset>(
      API_PATHS.store.tagsets.create,
      payload,
    )

    return unwrapData(response.data)
  },

  async updateTagset(id: string | number, payload: Partial<StoreTagsetPayload>): Promise<StoreTagset> {
    const response = await api.patch<ApiResponse<StoreTagset> | StoreTagset>(
      API_PATHS.store.tagsets.update(id),
      payload,
    )

    return unwrapData(response.data)
  },

  async deleteTagset(id: string | number): Promise<void> {
    await api.delete(API_PATHS.store.tagsets.delete(id))
  },

  async listTags(params?: StoreListParams & { tagset?: string | number; item_types?: string | number }): Promise<PaginatedResponse<StoreTag>> {
    const response = await api.get<
      ApiResponse<PaginatedResponse<StoreTag> | StoreTag[]>
      | PaginatedResponse<StoreTag>
      | StoreTag[]
    >(
      API_PATHS.store.tags.list,
      { params },
    )

    return toPaged<StoreTag>(unwrapData(response.data))
  },

  async listTagsForItemType(
    tagsetId: string | number,
    itemType: StoreTagItemType,
    params?: Omit<StoreListParams & { tagset?: string | number }, 'item_types' | 'tagset'>,
  ): Promise<PaginatedResponse<StoreTag>> {
    const response = await this.listTags({
      page: 1,
      page_size: 500,
      active: 1,
      tagset: tagsetId,
      item_types: itemType,
      ...params,
    })

    const results = [...(response.results ?? [])].sort((left, right) =>
      String(left.title ?? left.code ?? '').localeCompare(String(right.title ?? right.code ?? '')),
    )

    return {
      ...response,
      count: results.length,
      results,
    }
  },

  async createTag(payload: StoreTagPayload): Promise<StoreTag> {
    const response = await api.post<ApiResponse<StoreTag> | StoreTag>(
      API_PATHS.store.tags.create,
      payload,
    )

    return unwrapData(response.data)
  },

  async updateTag(id: string | number, payload: Partial<StoreTagPayload>): Promise<StoreTag> {
    const response = await api.patch<ApiResponse<StoreTag> | StoreTag>(
      API_PATHS.store.tags.update(id),
      payload,
    )

    return unwrapData(response.data)
  },

  async deleteTag(id: string | number): Promise<void> {
    await api.delete(API_PATHS.store.tags.delete(id))
  },

  async listTaggedItems(params?: StoreListParams & Partial<StoreTaggedItemPayload> & { active?: number | boolean; item_type?: string | number }): Promise<PaginatedResponse<StoreTaggedItem>> {
    const response = await api.get<
      ApiResponse<PaginatedResponse<StoreTaggedItem> | StoreTaggedItem[]>
      | PaginatedResponse<StoreTaggedItem>
      | StoreTaggedItem[]
    >(
      API_PATHS.store.taggedItems.list,
      { params },
    )

    return toPaged<StoreTaggedItem>(unwrapData(response.data))
  },

  async listTagLinkedItems(params?: StoreListParams & {
    tag?: string | number
    tagset?: string | number
    item_type?: string | number
    product?: string | number
    category?: string | number
    offer?: string | number
    store?: string | number
  }): Promise<PaginatedResponse<StoreTagLinkedItems>> {
    const response = await api.get<
      ApiResponse<PaginatedResponse<StoreTagLinkedItems> | StoreTagLinkedItems[]>
      | PaginatedResponse<StoreTagLinkedItems>
      | StoreTagLinkedItems[]
    >(
      API_PATHS.store.taggedItems.tagItemList,
      { params },
    )

    return toPaged<StoreTagLinkedItems>(unwrapData(response.data))
  },

  async updateTaggedItemStatus(id: string | number, payload: { active: number | boolean }): Promise<StoreTaggedItem> {
    const response = await api.patch<ApiResponse<StoreTaggedItem> | StoreTaggedItem>(
      API_PATHS.store.taggedItems.updateStatus(id),
      payload,
    )

    return unwrapData(response.data)
  },

  async tagProduct(payload: StoreTaggedItemPayload, options?: { service_instance_id?: string | number }): Promise<StoreTaggedItem> {
    const response = await api.post<ApiResponse<StoreTaggedItem> | StoreTaggedItem>(
      API_PATHS.store.taggedItems.tagProduct,
      payload,
      { params: { service_instance_id: options?.service_instance_id } },
    )

    return unwrapData(response.data)
  },

  async tagProductVariant(payload: StoreTaggedItemPayload, options?: { service_instance_id?: string | number }): Promise<StoreTaggedItem> {
    const response = await api.post<ApiResponse<StoreTaggedItem> | StoreTaggedItem>(
      API_PATHS.store.taggedItems.tagProductVariant,
      payload,
      { params: { service_instance_id: options?.service_instance_id } },
    )

    return unwrapData(response.data)
  },

  async tagCategory(payload: StoreTaggedItemPayload, options?: { service_instance_id?: string | number }): Promise<StoreTaggedItem> {
    const response = await api.post<ApiResponse<StoreTaggedItem> | StoreTaggedItem>(
      API_PATHS.store.taggedItems.tagCategory,
      payload,
      { params: { service_instance_id: options?.service_instance_id } },
    )

    return unwrapData(response.data)
  },

  async tagOffer(payload: StoreTaggedItemPayload, options?: { service_instance_id?: string | number }): Promise<StoreTaggedItem> {
    const response = await api.post<ApiResponse<StoreTaggedItem> | StoreTaggedItem>(
      API_PATHS.store.taggedItems.tagOffer,
      payload,
      { params: { service_instance_id: options?.service_instance_id } },
    )

    return unwrapData(response.data)
  },

  async tagStore(payload: StoreTaggedItemPayload, options?: { service_instance_id?: string | number }): Promise<StoreTaggedItem> {
    const response = await api.post<ApiResponse<StoreTaggedItem> | StoreTaggedItem>(
      API_PATHS.store.taggedItems.tagStore,
      payload,
      { params: { service_instance_id: options?.service_instance_id } },
    )

    return unwrapData(response.data)
  },

  async getVariantPrice(variantId: string | number): Promise<StoreProductVariantPrice | null> {
    const response = await api.get<ApiResponse<StoreProductVariantPrice> | StoreProductVariantPrice | null>(
      API_PATHS.store.variantPrices.detail(variantId),
    )

    const data = unwrapData(response.data)
    return data ?? null
  },

  async createVariantPrice(payload: StoreProductVariantPricePayload): Promise<StoreProductVariantPrice> {
    const response = await api.post<ApiResponse<StoreProductVariantPrice> | StoreProductVariantPrice>(API_PATHS.store.variantPrices.create, payload)
    return unwrapData(response.data)
  },

  async updateVariantPrice(id: string | number, payload: Partial<StoreProductVariantPricePayload>): Promise<StoreProductVariantPrice> {
    const response = await api.patch<ApiResponse<StoreProductVariantPrice> | StoreProductVariantPrice>(API_PATHS.store.variantPrices.update(id), payload)
    return unwrapData(response.data)
  },

  async deleteVariantPrice(id: string | number): Promise<void> {
    await api.delete(API_PATHS.store.variantPrices.delete(id))
  },

  async listAddonGroups(params?: StoreListParams & { product_id?: string | number; product_variant?: string | number; catalog_only?: boolean | number | string }): Promise<PaginatedResponse<StoreAddonGroup>> {
    const response = await api.get<ApiResponse<PaginatedResponse<StoreAddonGroup> | StoreAddonGroup[]> | PaginatedResponse<StoreAddonGroup> | StoreAddonGroup[]>(
      API_PATHS.store.addonGroups.list,
      { params },
    )

    return toPaged<StoreAddonGroup>(unwrapData(response.data))
  },

  async createAddonGroup(payload: StoreAddonGroupPayload): Promise<StoreAddonGroup> {
    const response = await api.post<ApiResponse<StoreAddonGroup> | StoreAddonGroup>(API_PATHS.store.addonGroups.create, payload)
    return unwrapData(response.data)
  },

  async updateAddonGroup(id: string | number, payload: Partial<StoreAddonGroupPayload>): Promise<StoreAddonGroup> {
    const response = await api.patch<ApiResponse<StoreAddonGroup> | StoreAddonGroup>(API_PATHS.store.addonGroups.update(id), payload)
    return unwrapData(response.data)
  },

  async deleteAddonGroup(id: string | number): Promise<void> {
    await api.delete(API_PATHS.store.addonGroups.delete(id))
  },

  async listVariantAddonGroupLinks(params?: { product_variant?: string | number; addon_group_id?: string | number }): Promise<StoreVariantAddonGroupLink[]> {
    const response = await api.get<
      ApiResponse<StoreVariantAddonGroupLink[] | PaginatedResponse<StoreVariantAddonGroupLink>>
      | StoreVariantAddonGroupLink[]
      | PaginatedResponse<StoreVariantAddonGroupLink>
    >(
      API_PATHS.store.variantAddonGroups.list,
      { params },
    )
    return unwrapListPayload<StoreVariantAddonGroupLink>(response.data)
  },

  async linkVariantAddonGroup(payload: StoreVariantAddonGroupLinkPayload): Promise<StoreVariantAddonGroupLink> {
    const response = await api.post<ApiResponse<StoreVariantAddonGroupLink> | StoreVariantAddonGroupLink>(
      API_PATHS.store.variantAddonGroups.create,
      payload,
    )
    return unwrapData(response.data)
  },

  async unlinkVariantAddonGroup(id: string | number): Promise<void> {
    await api.delete(API_PATHS.store.variantAddonGroups.delete(id))
  },

  async listVariantAddonItemLinks(params?: { product_variant?: string | number; addon_item_id?: string | number }): Promise<StoreVariantAddonItemLink[]> {
    const response = await api.get<
      ApiResponse<StoreVariantAddonItemLink[] | PaginatedResponse<StoreVariantAddonItemLink>>
      | StoreVariantAddonItemLink[]
      | PaginatedResponse<StoreVariantAddonItemLink>
    >(
      API_PATHS.store.variantAddonItems.list,
      { params },
    )
    return unwrapListPayload<StoreVariantAddonItemLink>(response.data)
  },

  async linkVariantAddonItem(payload: StoreVariantAddonItemLinkPayload): Promise<StoreVariantAddonItemLink> {
    const response = await api.post<ApiResponse<StoreVariantAddonItemLink> | StoreVariantAddonItemLink>(
      API_PATHS.store.variantAddonItems.create,
      payload,
    )
    return unwrapData(response.data)
  },

  async unlinkVariantAddonItem(id: string | number): Promise<void> {
    await api.delete(API_PATHS.store.variantAddonItems.delete(id))
  },

  async listAddons(params?: StoreListParams & { addon_group_id?: string | number; product_id?: string | number }): Promise<PaginatedResponse<StoreAddonItem>> {
    const response = await api.get<ApiResponse<PaginatedResponse<StoreAddonItem> | StoreAddonItem[]> | PaginatedResponse<StoreAddonItem> | StoreAddonItem[]>(
      API_PATHS.store.addons.list,
      { params },
    )
    return toPaged<StoreAddonItem>(unwrapData(response.data))
  },

  async createAddon(payload: StoreAddonItemPayload): Promise<StoreAddonItem> {
    const response = await api.post<ApiResponse<StoreAddonItem> | StoreAddonItem>(API_PATHS.store.addons.create, payload)
    return unwrapData(response.data)
  },

  async updateAddon(id: string | number, payload: Partial<StoreAddonItemPayload>): Promise<StoreAddonItem> {
    const response = await api.patch<ApiResponse<StoreAddonItem> | StoreAddonItem>(API_PATHS.store.addons.update(id), payload)
    return unwrapData(response.data)
  },

  async deleteAddon(id: string | number): Promise<void> {
    await api.delete(API_PATHS.store.addons.delete(id))
  },

  async listOffers(params?: StoreListParams): Promise<PaginatedResponse<StoreOffer>> {
    const response = await api.get<ApiResponse<PaginatedResponse<StoreOffer> | StoreOffer[]> | PaginatedResponse<StoreOffer> | StoreOffer[]>(
      API_PATHS.store.offers.list,
      { params },
    )

    return toPaged<StoreOffer>(unwrapData(response.data))
  },

  async getOffer(id: string | number, params?: StoreListParams): Promise<StoreOffer> {
    const response = await api.get<ApiResponse<StoreOffer> | StoreOffer>(
      API_PATHS.store.offers.detail(id),
      { params },
    )

    return unwrapData(response.data)
  },

  async listOfferSchemes(params?: StoreListParams): Promise<PaginatedResponse<StoreOfferScheme>> {
    const response = await api.get<ApiResponse<PaginatedResponse<StoreOfferScheme> | StoreOfferScheme[]> | PaginatedResponse<StoreOfferScheme> | StoreOfferScheme[]>(
      API_PATHS.store.offers.schemes.list,
      { params },
    )

    return toPaged<StoreOfferScheme>(unwrapData(response.data))
  },

  async createOfferScheme(payload: StoreOfferSchemePayload): Promise<StoreOfferScheme> {
    const response = await api.post<ApiResponse<StoreOfferScheme> | StoreOfferScheme>(API_PATHS.store.offers.schemes.create, payload)
    return unwrapData(response.data)
  },

  async updateOfferScheme(id: string | number, payload: Partial<StoreOfferSchemePayload>): Promise<StoreOfferScheme> {
    const response = await api.patch<ApiResponse<StoreOfferScheme> | StoreOfferScheme>(API_PATHS.store.offers.schemes.update(id), payload)
    return unwrapData(response.data)
  },

  async deleteOfferScheme(id: string | number): Promise<void> {
    await api.delete(API_PATHS.store.offers.schemes.delete(id))
  },

  async getOfferTypes(): Promise<Array<{ id: string | number; label: string }>> {
    const response = await api.get<ApiResponse<Array<Record<string, unknown>>> | Array<Record<string, unknown>>>(API_PATHS.store.offers.types)
    const payload = toList<Record<string, unknown>>(unwrapData(response.data))

    return payload.map((entry) => ({
      id: (entry.id as string | number | undefined) ?? '',
      label: String(entry.value ?? entry.label ?? entry.name ?? 'Offer type'),
    }))
  },

  async getOfferBenefitTypes(): Promise<Array<{ id: string | number; label: string }>> {
    const response = await api.get<ApiResponse<Array<Record<string, unknown>>> | Array<Record<string, unknown>>>(API_PATHS.store.offers.benefitTypes)
    const payload = toList<Record<string, unknown>>(unwrapData(response.data))

    return payload
      .map((entry) => ({
        id: (entry.id as string | number | undefined) ?? '',
        label: String(entry.value ?? entry.label ?? entry.name ?? 'Benefit'),
      }))
      .filter((entry) => {
        const label = entry.label.toLowerCase()
        return label === 'percentage' || label === 'flat'
      })
  },

  async getOfferPlatformTypes(): Promise<Array<{ id: string | number; label: string }>> {
    const response = await api.get<ApiResponse<Array<Record<string, unknown>>> | Array<Record<string, unknown>>>(
      API_PATHS.store.offers.platformTypes,
    )
    const payload = toList<Record<string, unknown>>(unwrapData(response.data))

    return payload.map((entry) => ({
      id: (entry.id as string | number | undefined) ?? '',
      label: String(entry.value ?? entry.label ?? entry.name ?? 'Platform'),
    }))
  },

  async createOffer(payload: StoreOfferPayload): Promise<StoreOffer> {
    const response = await api.post<ApiResponse<StoreOffer> | StoreOffer>(API_PATHS.store.offers.create, payload)
    return unwrapData(response.data)
  },

  async updateOffer(id: string | number, payload: Partial<StoreOfferPayload>): Promise<StoreOffer> {
    const response = await api.patch<ApiResponse<StoreOffer> | StoreOffer>(API_PATHS.store.offers.update(id), payload)
    return unwrapData(response.data)
  },

  async deleteOffer(id: string | number): Promise<void> {
    await api.delete(API_PATHS.store.offers.delete(id))
  },

  async listOfferProducts(params?: StoreListParams & { offer?: string | number }): Promise<PaginatedResponse<StoreOfferProduct>> {
    const response = await api.get<ApiResponse<PaginatedResponse<StoreOfferProduct> | StoreOfferProduct[]> | PaginatedResponse<StoreOfferProduct> | StoreOfferProduct[]>(
      API_PATHS.store.offers.offerProducts.list,
      { params },
    )
    return toPaged<StoreOfferProduct>(unwrapData(response.data))
  },

  async addOfferProduct(payload: StoreOfferProductPayload): Promise<StoreOfferProduct> {
    const response = await api.post<ApiResponse<StoreOfferProduct> | StoreOfferProduct>(
      API_PATHS.store.offers.offerProducts.add,
      payload,
    )
    return unwrapData(response.data)
  },

  async removeOfferProduct(id: string | number): Promise<void> {
    await api.delete(API_PATHS.store.offers.offerProducts.remove(id))
  },

  async getOrderStatuses(params?: {
    service_instance_id?: string | number
    org_instance_id?: string | number
    service_variant_type?: string | number
    order_type_id?: string | number
    serve_type_id?: string | number
    order_type?: string | number
  }): Promise<Array<{ key: string | number; label: string }>> {
    const response = await api.get<ApiResponse<Array<Record<string, unknown>>> | Array<Record<string, unknown>>>(
      API_PATHS.orders.statuses,
      { params },
    )

    const payload = toList<Record<string, unknown>>(unwrapData(response.data))

    return payload.map((entry) => ({
      key: (entry.status as string | number | undefined) ?? (entry.key as string | number | undefined) ?? '',
      label: String(entry.label ?? entry.name ?? entry.key ?? entry.status ?? 'Unknown'),
    }))
  },

  async updateOrderStatus(payload: Record<string, unknown>): Promise<void> {
    await api.post(API_PATHS.orders.updateStatus, payload)
  },

  async updateOrderStatusById(id: string | number, payload: Record<string, unknown>): Promise<void> {
    await api.post(API_PATHS.orders.updateStatusById(id), payload)
  },

  async updatePosOrderById(id: string | number, payload: Record<string, unknown>): Promise<void> {
    await api.patch(API_PATHS.store.pos.updateOrder(id), payload)
  },

  async getOrderTypes(): Promise<Array<{ id: string | number; label: string }>> {
    const response = await api.get<ApiResponse<Array<Record<string, unknown>>> | Array<Record<string, unknown>>>(API_PATHS.store.pos.orderTypes)
    const payload = toList<Record<string, unknown>>(unwrapData(response.data))

    return payload.map((entry) => ({
      id: (entry.id as string | number | undefined) ?? (entry.value as string | number | undefined) ?? '',
      label: String(entry.label ?? entry.name ?? entry.value ?? 'Order type'),
    }))
  },

  async getOrderTypesManaged(params?: {
    service_instance_id?: string | number
    org_instance_id?: string | number
    service_variant_type?: string | number
    is_system?: boolean
    active?: boolean
    simple?: boolean
    /** When true, skips auto-injection of service_variant_type so all variants' order types are returned. */
    skipVariantInference?: boolean
  }): Promise<Array<{ id: string | number; key: string; label: string; description?: string; sorting_order?: number; is_system?: boolean; active?: boolean; address_required?: boolean; service_instance?: string | number | null; org_instance?: string | number | null }>> {
    const rawParams = {
      service_instance_id: params?.service_instance_id,
      org_instance_id: params?.org_instance_id,
      service_variant_type: params?.service_variant_type,
      is_system: params?.is_system,
      active: params?.active,
      simple: params?.simple,
    }
    const normalizedParams = params?.skipVariantInference
      ? rawParams
      : withVariantTypeParam(rawParams, { service_instance_id: params?.service_instance_id })
    const response = await api.get<ApiResponse<Array<Record<string, unknown>>> | Array<Record<string, unknown>>>(
      API_PATHS.store.orderTypes.list,
      { params: normalizedParams }
    )
    const payload = toList<Record<string, unknown>>(unwrapData(response.data))

    return payload.map((entry) => ({
      id: entry.id as string | number,
      key: String(entry.key ?? ''),
      label: String(entry.label ?? ''),
      description: entry.description ? String(entry.description) : undefined,
      sorting_order: typeof entry.sorting_order === 'number' ? entry.sorting_order : 0,
      is_system: Boolean(entry.is_system),
      active: entry.active !== undefined ? Boolean(entry.active) : true,
      address_required: entry.address_required !== undefined ? Boolean(entry.address_required) : false,
      service_instance: (entry.service_instance as string | number | null | undefined) ?? null,
      org_instance: (entry.org_instance as string | number | null | undefined) ?? null,
    }))
  },

  async getServeTypes(params?: {
    service_instance_id?: string | number
    org_instance_id?: string | number
    service_variant_type?: string | number
    is_system?: boolean
    active?: boolean
    simple?: boolean
    skipVariantInference?: boolean
  }) {
    return this.getOrderTypesManaged(params)
  },

  async createOrderType(payload: {
    key: string
    label: string
    description?: string
    address_required?: boolean
    service_instance_id?: string | number
    service_instance?: string | number
    org_instance?: string | number
    whole_organization?: boolean
    service_variant_type?: string | number
    sorting_order?: number
  }): Promise<Record<string, unknown>> {
    const normalizedPayload = withVariantTypePayload(payload)
    const response = await api.post<ApiResponse<Record<string, unknown>> | Record<string, unknown>>(
      API_PATHS.store.orderTypes.list,
      normalizedPayload
    )
    return unwrapData(response.data)
  },

  async createServeType(payload: {
    key: string
    label: string
    description?: string
    address_required?: boolean
    service_instance_id?: string | number
    service_instance?: string | number
    org_instance?: string | number
    whole_organization?: boolean
    service_variant_type?: string | number
    sorting_order?: number
  }) {
    return this.createOrderType(payload)
  },

  async updateOrderType(id: string | number, payload: {
    label?: string
    description?: string
    sorting_order?: number
    active?: boolean
    address_required?: boolean
  }): Promise<Record<string, unknown>> {
    const normalizedPayload = {
      ...payload,
      is_active: payload.active,
    }
    const response = await api.patch<ApiResponse<Record<string, unknown>> | Record<string, unknown>>(
      API_PATHS.store.orderTypes.detail(id),
      normalizedPayload,
    )
    return unwrapData(response.data)
  },

  async updateServeType(id: string | number, payload: {
    label?: string
    description?: string
    sorting_order?: number
    active?: boolean
    address_required?: boolean
  }) {
    return this.updateOrderType(id, payload)
  },

  async activateOrderType(id: string | number): Promise<void> {
    await api.post(API_PATHS.store.orderTypes.activate(id))
  },

  async activateServeType(id: string | number): Promise<void> {
    await this.activateOrderType(id)
  },

  async deactivateOrderType(id: string | number): Promise<void> {
    await api.post(API_PATHS.store.orderTypes.deactivate(id))
  },

  async deactivateServeType(id: string | number): Promise<void> {
    await this.deactivateOrderType(id)
  },

  async deleteOrderType(id: string | number): Promise<void> {
    await api.delete(API_PATHS.store.orderTypes.detail(id))
  },

  async deleteServeType(id: string | number): Promise<void> {
    await this.deleteOrderType(id)
  },

  async getManagedOrderStatuses(params?: {
    service_instance_id?: string | number
    org_instance_id?: string | number
    service_variant_type?: string | number
    is_system?: boolean
    active?: boolean
    simple?: boolean
  }): Promise<Array<{ id: string | number; status_value: string | number; label: string; description?: string; sorting_order?: number; is_system?: boolean; is_default?: boolean; is_terminal?: boolean; active?: boolean; service_instance?: string | number | null; org_instance?: string | number | null }>> {
    const normalizedParams = withVariantTypeParam(
      {
        service_instance_id: params?.service_instance_id,
        service_variant_type: params?.service_variant_type,
        is_system: params?.is_system,
        active: params?.active,
        simple: params?.simple,
      },
      { service_instance_id: params?.service_instance_id }
    )
    const response = await api.get<ApiResponse<Array<Record<string, unknown>>> | Array<Record<string, unknown>>>(
      API_PATHS.store.orderStatusTypes.list,
      { params: normalizedParams }
    )
    const payload = toList<Record<string, unknown>>(unwrapData(response.data))

    return payload.map((entry) => ({
      id: entry.id as string | number,
      status_value: (entry.status_value as string | number | undefined) ?? '',
      label: String(entry.label ?? ''),
      description: entry.description ? String(entry.description) : undefined,
      sorting_order: typeof entry.sorting_order === 'number' ? entry.sorting_order : 0,
      is_system: Boolean(entry.is_system),
      is_default: Boolean(entry.is_default),
      is_terminal: Boolean(entry.is_terminal),
      active: entry.active !== undefined ? Boolean(entry.active) : true,
      service_instance: (entry.service_instance as string | number | null | undefined) ?? null,
      org_instance: (entry.org_instance as string | number | null | undefined) ?? null,
    }))
  },

  async createManagedOrderStatus(payload: {
    status_value: string | number
    label: string
    description?: string
    service_instance_id?: string | number
    service_instance?: string | number
    org_instance?: string | number
    whole_organization?: boolean
    service_variant_type?: string | number
    sorting_order?: number
    is_terminal?: boolean
  }): Promise<Record<string, unknown>> {
    const normalizedPayload = withVariantTypePayload(payload)
    const response = await api.post<ApiResponse<Record<string, unknown>> | Record<string, unknown>>(
      API_PATHS.store.orderStatusTypes.list,
      normalizedPayload
    )
    return unwrapData(response.data)
  },

  async updateManagedOrderStatus(id: string | number, payload: {
    status_value?: string | number
    label?: string
    description?: string
    sorting_order?: number
    active?: boolean
    is_terminal?: boolean
  }): Promise<Record<string, unknown>> {
    const response = await api.patch<ApiResponse<Record<string, unknown>> | Record<string, unknown>>(
      API_PATHS.store.orderStatusTypes.detail(id),
      payload
    )
    return unwrapData(response.data)
  },

  async activateManagedOrderStatus(id: string | number): Promise<void> {
    await api.post(API_PATHS.store.orderStatusTypes.activate(id))
  },

  async deactivateManagedOrderStatus(id: string | number): Promise<void> {
    await api.post(API_PATHS.store.orderStatusTypes.deactivate(id))
  },

  async deleteManagedOrderStatus(id: string | number): Promise<void> {
    await api.delete(`/api/store/order-status-types/${id}/`)
  },

  async setManagedOrderStatusDefault(id: string | number, isDefault: boolean): Promise<void> {
    const orderStatusPaths = API_PATHS.store.orderStatusTypes as {
      setDefault?: (statusId: string | number) => string
      unsetDefault?: (statusId: string | number) => string
    }
    const setDefaultPath =
      typeof orderStatusPaths.setDefault === 'function'
        ? orderStatusPaths.setDefault(id)
        : `/api/store/order-status-types/${id}/set_as_default/`
    const unsetDefaultPath =
      typeof orderStatusPaths.unsetDefault === 'function'
        ? orderStatusPaths.unsetDefault(id)
        : `/api/store/order-status-types/${id}/unset_default/`

    if (isDefault) {
      await api.post(setDefaultPath)
    } else {
      await api.post(unsetDefaultPath)
    }
  },

  async getAllowedOrderStatusTransitions(orderId: string | number): Promise<Array<{ key: string; status?: number; label: string }>> {
    const response = await api.get<ApiResponse<{ results?: Array<Record<string, unknown>> }> | { results?: Array<Record<string, unknown>> }>(
      API_PATHS.store.orderStatusTransitions.allowedNext,
      { params: { order: orderId, actor: 'order_center' } },
    )
    const payload = unwrapData(response.data)
    const rows = Array.isArray(payload.results) ? payload.results : []

    return rows.map((entry) => ({
      key: String((entry.key as string | number | undefined) ?? ''),
      status: typeof entry.status === 'number' ? entry.status : Number(entry.status) || undefined,
      label: String(entry.label ?? entry.name ?? entry.key ?? entry.status ?? 'Unknown'),
    }))
  },

  async listOrderStatusTransitions(params?: {
    service_instance_id?: string | number
    org_instance_id?: string | number
    service_variant_type?: string | number
    order_type?: string | number
    serve_type?: string | number
    from_status?: string | number
    actor?: string
  }): Promise<Array<Record<string, unknown>>> {
    const normalizedParams = withVariantTypeParam(
      {
        service_instance_id: params?.service_instance_id,
        service_variant_type: params?.service_variant_type,
        order_type: params?.order_type ?? params?.serve_type,
        from_status: params?.from_status,
        actor: params?.actor,
      },
      { service_instance_id: params?.service_instance_id }
    )
    const response = await api.get<ApiResponse<Array<Record<string, unknown>>> | Array<Record<string, unknown>>>(
      API_PATHS.store.orderStatusTransitions.list,
      { params: normalizedParams },
    )
    return toList<Record<string, unknown>>(unwrapData(response.data))
  },

  async createOrderStatusTransition(payload: Record<string, unknown>): Promise<Record<string, unknown>> {
    const normalizedPayload = withVariantTypePayload(payload)
    const response = await api.post<ApiResponse<Record<string, unknown>> | Record<string, unknown>>(
      API_PATHS.store.orderStatusTransitions.list,
      normalizedPayload,
    )
    return unwrapData(response.data)
  },

  async updateOrderStatusTransition(id: string | number, payload: Record<string, unknown>): Promise<Record<string, unknown>> {
    const response = await api.patch<ApiResponse<Record<string, unknown>> | Record<string, unknown>>(
      API_PATHS.store.orderStatusTransitions.detail(id),
      payload,
    )
    return unwrapData(response.data)
  },

  async deleteOrderStatusTransition(id: string | number): Promise<void> {
    await api.delete(API_PATHS.store.orderStatusTransitions.detail(id))
  },

  async createPosOrder(payload: StorePosOrderPayload): Promise<Record<string, unknown>> {
    const response = await api.post<ApiResponse<Record<string, unknown>> | Record<string, unknown>>(API_PATHS.store.pos.createOrder, payload)
    return unwrapData(response.data)
  },

  // ---- Proportion Groups (Service Unit Types) ----

  async listProportionGroups(params?: StoreListParams): Promise<Array<Record<string, unknown>>> {
    const response = await api.get<ApiResponse<unknown> | unknown>(API_PATHS.store.proportionGroups.list, { params })
    return toList<Record<string, unknown>>(unwrapData(response.data as ApiResponse<unknown>))
  },

  async createProportionGroup(payload: Record<string, unknown>): Promise<Record<string, unknown>> {
    const response = await api.post<ApiResponse<Record<string, unknown>> | Record<string, unknown>>(API_PATHS.store.proportionGroups.create, payload)
    return unwrapData(response.data)
  },

  async updateProportionGroup(id: string | number, payload: Record<string, unknown>): Promise<Record<string, unknown>> {
    const response = await api.patch<ApiResponse<Record<string, unknown>> | Record<string, unknown>>(API_PATHS.store.proportionGroups.update(id), payload)
    return unwrapData(response.data)
  },

  async deleteProportionGroup(id: string | number): Promise<void> {
    await api.delete(API_PATHS.store.proportionGroups.delete(id))
  },

  // ---- Proportions (Unit Requirements per product) ----

  async listProportions(params?: StoreListParams & { proportion_group_id?: string | number; product_id?: string | number; product_variant?: string | number }): Promise<Array<Record<string, unknown>>> {
    const response = await api.get<ApiResponse<unknown> | unknown>(API_PATHS.store.proportions.list, { params })
    return toList<Record<string, unknown>>(unwrapData(response.data as ApiResponse<unknown>))
  },

  async createProportion(payload: Record<string, unknown>): Promise<Record<string, unknown>> {
    const response = await api.post<ApiResponse<Record<string, unknown>> | Record<string, unknown>>(API_PATHS.store.proportions.create, payload)
    return unwrapData(response.data)
  },

  async updateProportion(id: string | number, payload: Record<string, unknown>): Promise<Record<string, unknown>> {
    const response = await api.patch<ApiResponse<Record<string, unknown>> | Record<string, unknown>>(API_PATHS.store.proportions.update(id), payload)
    return unwrapData(response.data)
  },

  async deleteProportion(id: string | number): Promise<void> {
    await api.delete(API_PATHS.store.proportions.delete(id))
  },

  // ---- Variant Proportions (Unit Requirements per package/variant) ----

  async listVariantProportions(params?: StoreListParams & { product_variant?: string | number; product_variant_id?: string | number }): Promise<Array<Record<string, unknown>>> {
    const normalizedParams = { ...(params ?? {}) }
    if (normalizedParams.product_variant_id == null && normalizedParams.product_variant != null) {
      normalizedParams.product_variant_id = normalizedParams.product_variant
    }
    delete normalizedParams.product_variant
    const response = await api.get<ApiResponse<unknown> | unknown>(API_PATHS.store.variantProportions.list, { params: normalizedParams })
    return toList<Record<string, unknown>>(unwrapData(response.data as ApiResponse<unknown>))
  },

  async createVariantProportion(payload: Record<string, unknown>): Promise<Record<string, unknown>> {
    const normalizedPayload = { ...payload }
    if (normalizedPayload.product_variant == null && normalizedPayload.product_variant_id != null) {
      normalizedPayload.product_variant = normalizedPayload.product_variant_id
    }
    const response = await api.post<ApiResponse<Record<string, unknown>> | Record<string, unknown>>(API_PATHS.store.variantProportions.create, normalizedPayload)
    return unwrapData(response.data)
  },

  async updateVariantProportion(id: string | number, payload: Record<string, unknown>): Promise<Record<string, unknown>> {
    const normalizedPayload = { ...payload }
    if (normalizedPayload.product_variant == null && normalizedPayload.product_variant_id != null) {
      normalizedPayload.product_variant = normalizedPayload.product_variant_id
    }
    const response = await api.patch<ApiResponse<Record<string, unknown>> | Record<string, unknown>>(API_PATHS.store.variantProportions.update(id), normalizedPayload)
    return unwrapData(response.data)
  },

  async deleteVariantProportion(id: string | number): Promise<void> {
    await api.delete(API_PATHS.store.variantProportions.delete(id))
  },

  /** Basket list runs through `apply_offer_basket`; payload includes `results`, totals, payment modes, etc. */
  async listBasket(params?: Record<string, unknown>): Promise<Record<string, unknown>> {
    const response = await api.get<ApiResponse<Record<string, unknown>> | Record<string, unknown>>(
      API_PATHS.store.basket.list,
      { params },
    )
    return unwrapData(response.data as ApiResponse<Record<string, unknown>>)
  },

  async addToBasket(payload: Record<string, unknown>): Promise<unknown> {
    const response = await api.post<ApiResponse<unknown> | unknown>(API_PATHS.store.basket.add, payload)
    return unwrapData(response.data as ApiResponse<unknown>)
  },

  /** `id` from basket list serializer (Fernet token), not `_id` mask — backend runs `basket_decryption` on the path. */
  async updateBasketQuantity(basketEncryptedRowId: string | number, quantity: number): Promise<unknown> {
    const response = await api.patch<ApiResponse<unknown> | unknown>(
      API_PATHS.store.basket.update(basketEncryptedRowId),
      {
        quantity,
      },
    )
    return unwrapData(response.data as ApiResponse<unknown>)
  },

  async updateBasketItem(basketEncryptedRowId: string | number, payload: Record<string, unknown>): Promise<unknown> {
    const response = await api.patch<ApiResponse<unknown> | unknown>(
      API_PATHS.store.basket.update(basketEncryptedRowId),
      payload,
    )
    return unwrapData(response.data as ApiResponse<unknown>)
  },

  /** DELETE Store/Basket/Remove/<id>/ — use encrypted basket row id (same as PATCH quantity), not catalog variant id. */
  async removeBasketProductVariant(basketEncryptedRowId: string | number): Promise<unknown> {
    const response = await api.delete<ApiResponse<unknown> | unknown>(
      API_PATHS.store.basket.remove(basketEncryptedRowId),
    )
    return unwrapData(response.data as ApiResponse<unknown>)
  },

  async listOrderItemMedia(params?: { order_item?: string | number; basket_item?: string | number }): Promise<StoreOrderItemMedia[]> {
    const response = await api.get<ApiResponse<StoreOrderItemMedia[]> | StoreOrderItemMedia[]>(
      API_PATHS.orders.orderItemMediaList,
      { params },
    )
    return toList<StoreOrderItemMedia>(unwrapData(response.data)).map(normalizeOrderItemMediaRecord)
  },

  async uploadOrderItemMedia(payload: {
    files: File[]
    order_item?: string | number
    basket_item?: string | number
    type?: string | number
  }): Promise<StoreOrderItemMedia[]> {
    const formData = new FormData()
    for (const file of payload.files) {
      formData.append('file', file)
    }

    if (payload.order_item != null) {
      formData.append('order_item', String(payload.order_item))
    }
    if (payload.basket_item != null) {
      formData.append('basket_item', String(payload.basket_item))
    }
    if (payload.type != null) {
      formData.append('type', String(payload.type))
    }

    const response = await api.post<ApiResponse<StoreOrderItemMedia[]> | StoreOrderItemMedia[]>(
      API_PATHS.orders.orderItemMediaCreate,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    )
    return toList<StoreOrderItemMedia>(unwrapData(response.data)).map(normalizeOrderItemMediaRecord)
  },

  async listProductFeatures(params?: {
    scope_type?: string
    search?: string
  }): Promise<StoreProductFeature[]> {
    const response = await api.get<ApiResponse<StoreProductFeature[]>>(
      API_PATHS.store.productFeatures.list,
      { params },
    )
    return toList<StoreProductFeature>(unwrapData(response.data as ApiResponse<StoreProductFeature[]>))
  },

  async createProductFeature(
    payload: StoreProductFeaturePayload,
  ): Promise<StoreProductFeature> {
    const response = await api.post<ApiResponse<StoreProductFeature>>(
      API_PATHS.store.productFeatures.create,
      payload,
    )
    return unwrapData(response.data as ApiResponse<StoreProductFeature>)
  },

  async updateProductFeature(
    id: string | number,
    payload: Partial<StoreProductFeaturePayload>,
  ): Promise<StoreProductFeature> {
    const response = await api.patch<ApiResponse<StoreProductFeature>>(
      API_PATHS.store.productFeatures.update(id),
      payload,
    )
    return unwrapData(response.data as ApiResponse<StoreProductFeature>)
  },

  async deleteProductFeature(id: string | number): Promise<void> {
    await api.delete(API_PATHS.store.productFeatures.delete(id))
  },

  async bootstrapProductFeatures(
    features: Omit<StoreProductFeaturePayload, 'scope_type'>[],
  ): Promise<{ created_count: number; skipped_codes: string[] }> {
    const response = await api.post<ApiResponse<{ created_count: number; skipped_codes: string[] }>>(
      API_PATHS.store.productFeatures.bootstrap,
      { features },
    )
    return unwrapData(response.data as ApiResponse<{ created_count: number; skipped_codes: string[] }>)
  },

  async getProductFeatureAssignments(
    productId: string | number,
  ): Promise<StoreProductFeatureAssignmentsResponse> {
    const response = await api.get<ApiResponse<StoreProductFeatureAssignmentsResponse>>(
      API_PATHS.store.productFeatures.productAssignments(productId),
    )
    return unwrapData(response.data as ApiResponse<StoreProductFeatureAssignmentsResponse>) ?? {
      included: [],
      excluded: [],
    }
  },

  async setProductFeatureAssignments(
    productId: string | number,
    payload: StoreProductFeatureAssignmentSet,
  ): Promise<void> {
    await api.post(
      API_PATHS.store.productFeatures.setProductAssignments(productId),
      payload,
    )
  },

  async getVariantFeatureAssignments(
    variantId: string | number,
  ): Promise<StoreVariantFeatureAssignmentsResponse> {
    const response = await api.get<ApiResponse<StoreVariantFeatureAssignmentsResponse>>(
      API_PATHS.store.productFeatures.variantAssignments(variantId),
    )
    return unwrapData(response.data as ApiResponse<StoreVariantFeatureAssignmentsResponse>) ?? {
      variant_included: [],
      variant_excluded: [],
      effective_included: [],
      effective_excluded: [],
    }
  },

  async setVariantFeatureAssignments(
    variantId: string | number,
    payload: StoreProductFeatureAssignmentSet,
  ): Promise<void> {
    await api.post(
      API_PATHS.store.productFeatures.setVariantAssignments(variantId),
      payload,
    )
  },
}

export default storeCommerceApi
